import { useEffect, useMemo, useSyncExternalStore } from "react";
import {
  getLocalizedProfileSubscriptionOffer,
  inferSubscriptionCountryCode
} from "./subscriptionCountryPricing.js";
import { subscriptionCountryPricingData } from "./subscriptionCountryPricingData.js";

const visitorPricingCountryUrl = "https://api.country.is/";
const visitorPricingCountryStorageKey = "badminta:visitor-pricing-country:v1";
const visitorPricingCountryCacheMs = 12 * 60 * 60 * 1000;
const visitorPricingCountryRetryMs = 5 * 60 * 1000;

const supportedCountryCode = (value = "") => {
  const countryCode = String(value || "").trim().toUpperCase();
  return subscriptionCountryPricingData[countryCode] ? countryCode : "";
};

const readCachedVisitorCountry = () => {
  if (typeof window === "undefined") return null;
  try {
    const cached = JSON.parse(window.localStorage.getItem(visitorPricingCountryStorageKey) || "null");
    const countryCode = supportedCountryCode(cached?.countryCode);
    const expiresAt = Number(cached?.expiresAt || 0);
    if (countryCode && expiresAt > Date.now()) {
      return {
        countryCode,
        resolvedAt: Number(cached?.resolvedAt || Date.now())
      };
    }
    window.localStorage.removeItem(visitorPricingCountryStorageKey);
  } catch {
    // Local pricing still works from browser locale when storage is unavailable.
  }
  return null;
};

const cachedVisitorCountry = readCachedVisitorCountry();
const localeCountryCode = inferSubscriptionCountryCode("AU");
const serverSnapshot = Object.freeze({
  countryCode: "AU",
  source: "platform_default",
  status: "resolving",
  resolvedAt: 0
});

let visitorCountrySnapshot = Object.freeze(cachedVisitorCountry
  ? {
      countryCode: cachedVisitorCountry.countryCode,
      source: "ip_cache",
      status: "ready",
      resolvedAt: cachedVisitorCountry.resolvedAt
    }
  : {
      countryCode: localeCountryCode,
      source: "browser_locale",
      status: typeof window === "undefined" ? "ready" : "resolving",
      resolvedAt: 0
    });
let visitorCountryRequest = null;
let visitorCountryLastAttemptAt = 0;
const visitorCountryListeners = new Set();

const publishVisitorCountry = (next) => {
  visitorCountrySnapshot = Object.freeze(next);
  visitorCountryListeners.forEach((listener) => listener());
  return visitorCountrySnapshot;
};

const cacheVisitorCountry = (countryCode, resolvedAt) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(visitorPricingCountryStorageKey, JSON.stringify({
      countryCode,
      resolvedAt,
      expiresAt: resolvedAt + visitorPricingCountryCacheMs
    }));
  } catch {
    // A blocked storage write must not block localized pricing.
  }
};

export const resolveVisitorPricingCountry = async ({
  fetcher = globalThis.fetch,
  timeoutMs = 2500,
  force = false
} = {}) => {
  if (typeof window === "undefined" || typeof fetcher !== "function") return visitorCountrySnapshot;
  const now = Date.now();
  if (!force && ["ip", "ip_cache"].includes(visitorCountrySnapshot.source)) return visitorCountrySnapshot;
  if (!force && visitorCountryRequest) return visitorCountryRequest;
  if (!force && visitorCountryLastAttemptAt && now - visitorCountryLastAttemptAt < visitorPricingCountryRetryMs) {
    return visitorCountrySnapshot;
  }

  visitorCountryLastAttemptAt = now;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeout = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
  visitorCountryRequest = (async () => {
    try {
      const response = await fetcher(visitorPricingCountryUrl, {
        method: "GET",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        headers: { Accept: "application/json" },
        ...(controller ? { signal: controller.signal } : {})
      });
      if (!response?.ok) throw new Error(`Country lookup failed with ${response?.status || "no response"}.`);
      const payload = await response.json();
      const countryCode = supportedCountryCode(payload?.country);
      if (!countryCode) throw new Error("Country lookup returned an unsupported country.");
      const resolvedAt = Date.now();
      cacheVisitorCountry(countryCode, resolvedAt);
      return publishVisitorCountry({
        countryCode,
        source: "ip",
        status: "ready",
        resolvedAt
      });
    } catch {
      return publishVisitorCountry({
        countryCode: supportedCountryCode(visitorCountrySnapshot.countryCode) || localeCountryCode,
        source: visitorCountrySnapshot.source === "ip_cache" ? "ip_cache" : "browser_locale",
        status: "fallback",
        resolvedAt: visitorCountrySnapshot.resolvedAt || 0
      });
    } finally {
      if (timeout) window.clearTimeout(timeout);
      visitorCountryRequest = null;
    }
  })();

  return visitorCountryRequest;
};

const subscribeVisitorPricingCountry = (listener) => {
  visitorCountryListeners.add(listener);
  return () => visitorCountryListeners.delete(listener);
};

const getVisitorPricingCountrySnapshot = () => visitorCountrySnapshot;
const getVisitorPricingCountryServerSnapshot = () => serverSnapshot;

export const useVisitorPricingCountry = () => {
  const snapshot = useSyncExternalStore(
    subscribeVisitorPricingCountry,
    getVisitorPricingCountrySnapshot,
    getVisitorPricingCountryServerSnapshot
  );

  useEffect(() => {
    resolveVisitorPricingCountry();
  }, []);

  return snapshot;
};

export const useLocalizedProfileSubscriptionOffer = (plan) => {
  const pricingCountry = useVisitorPricingCountry();
  const offer = useMemo(
    () => getLocalizedProfileSubscriptionOffer(plan, pricingCountry.countryCode),
    [plan, pricingCountry.countryCode]
  );
  return { offer, pricingCountry };
};

export const visitorPricingCountryConfig = Object.freeze({
  endpoint: visitorPricingCountryUrl,
  storageKey: visitorPricingCountryStorageKey,
  cacheMs: visitorPricingCountryCacheMs
});
